import React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { getFollowMeCanShareTickets } from '@core/Areas/FollowMe/helpers/api'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import type { Ticket, TicketResponseData } from '@core/Areas/AccountActivity/types'
import { isFauxTicketTransaction } from '@core/Areas/AccountActivity/helpers'
import { fetchPendingTicketsData } from '@core/Areas/AccountActivity/helpers/api'
import { shouldAllowShareInBetLoop } from '@core/Areas/FollowMe/helpers/shouldAllowShareInBetLoop'

export const useSharePendingTicketsData = () => {
  const { fetchNextPage, isFetchingNextPage, isLoading, data, isError, isFetching } =
    useInfiniteQuery({
      queryKey: queryKeys.followMeSharePendingTickets,
      queryFn: ({ pageParam }) => fetchTicketsData({ nextPage: pageParam }),
      initialPageParam: '',
      getNextPageParam: prevPage => {
        if (prevPage.isFinalPage) return undefined
        return prevPage._nextPage || null
      },
      staleTime: 0,
      refetchOnMount: 'always',
    })

  const tickets = data?.pages.flatMap(page => page.data) || []
  const isEmptyState = !isLoading && !isFetching && tickets.length === 0
  const shouldStopFetching =
    data && data.pages.length > 0 && (data.pages[data.pages.length - 1].isFinalPage || isEmptyState)

  return {
    tickets,
    isError,
    isLoading,
    isEmptyState,
    shouldStopFetching,
    LoadMoreButton: () => (
      <AutoFetchButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
    ),
  }
}

// =============
// Local Helpers
// =============

const fetchTicketsData = async ({
  nextPage,
}: {
  nextPage: string | null
}): Promise<FetchPendingTicketsData> => {
  const pendingTicketsData = await fetchPendingTicketsData({
    activeTab: 'pending',
    isOrderByEventStart: true,
    nextPage,
  })
  const tickets = Object.values(pendingTicketsData.ticketsById)
  const ticketSequences = Object.keys(pendingTicketsData.ticketsById)

  if (ticketSequences.length === 0)
    return {
      data: [],
      isFinalPage: true,
      _nextPage: null,
    }

  const canShareData = await getFollowMeCanShareTickets(ticketSequences)

  const shareableSequences = canShareData.flatMap(ticket =>
    ticket.canShare ? [ticket.ticketSequence] : []
  )

  if (shareableSequences.length === 0)
    return {
      data: [],
      isFinalPage: true,
      _nextPage: null,
    }

  const filteredTickets = tickets.flatMap(ticket => {
    if (isFauxTicketTransaction(ticket)) return []

    const shouldAllowShare =
      shareableSequences.includes(ticket.ticketSequence) &&
      shouldAllowShareInBetLoop({
        contextType: 'ticket',
        share: { ticket, activeTab: 'pending', ticketSequence: ticket.ticketSequence },
      })
    return shouldAllowShare ? [ticket] : []
  })

  return {
    data: filteredTickets,
    isFinalPage: pendingTicketsData.isFinalPage,
    _nextPage: pendingTicketsData._nextPage,
  }
}

// =====
// Types
// =====

type FetchPendingTicketsData = { data: Ticket[] } & Pick<
  TicketResponseData,
  '_nextPage' | 'isFinalPage'
>
